// VATCalculator.js
import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
const currentYear = new Date().getFullYear();
const TITLE = `Kalkulator Pajak Pertambahan Nilai - Hitung Pajak dengan Akurat`;
const DESCRIPTION = `Hitung pajak pertambahan nilai (PPN) ${currentYear} dengan akurat menggunakan kalkulator pajak gratis. Alat mudah untuk menghitung pajak penjualan dan pajak pembelian dengan menambahkan atau mengekstrak pajak dari jumlah berapa pun. Hitung pajak bulanan untuk bisnis dengan mudah dengan penjelasan rinci dan contoh praktis.`;
const KEYWORDS = `kalkulator pajak pertambahan nilai ${currentYear}, hitung pajak, VAT calculator, pajak penjualan, pajak pembelian, hitung pajak 15%, perhitungan pajak, tambah PPN, ekstrak pajak, pajak bisnis, kalkulator PPN, nilai pajak, jumlah termasuk pajak, pajak atas jasa`;
const OG_URL = "https://do-calculate.com/calculator/id/vat/";
function VATCalculator() {
  const [saleValue, setSaleValue] = useState("");
  const [vatRate, setVatRate] = useState("15");
  const [isSaleInclusiveOfVAT, setIsSaleInclusiveOfVAT] = useState("false");
  const [saleValueError, setSaleValueError] = useState("");
  const [vatRateError, setVatRateError] = useState("");
  const [vatResult, setVatResult] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [priceBeforeVat, setPriceBeforeVat] = useState("");
  const handleVatRateChange = (event) => {
    const inputVat = event.target.value;
    if (inputVat !== "") {
      const parsedVat = parseFloat(inputVat);
      if (!isNaN(parsedVat) && parsedVat >= 0 && parsedVat <= 100) {
        setVatRate(inputVat);
        setVatRateError("");
      } else {
        setVatRate(inputVat);
        if (isNaN(parsedVat)) {
          setVatRateError("Pastikan memasukkan angka yang valid");
        } else if (parsedVat < 0) {
          setVatRateError("Pajak tidak boleh negatif");
        } else if (parsedVat > 100) {
          setVatRateError("Pajak harus antara 0 - 100%");
        }
      }
    } else {
      setVatRate("");
      setVatRateError("");
    }
  };
  const handleSaleChange = (event) => {
    const inputSale = event.target.value;
    if (inputSale !== "") {
      const parsedSale = parseFloat(inputSale);
      if (!isNaN(parsedSale) && parsedSale >= 0) {
        setSaleValue(inputSale);
        setSaleValueError("");
      } else {
        setSaleValue(inputSale);
        if (isNaN(parsedSale)) {
          setSaleValueError("Pastikan memasukkan angka yang valid");
        } else if (parsedSale < 0) {
          setSaleValueError("Nilai tidak boleh negatif");
        }
      }
    } else {
      setSaleValue("");
      setSaleValueError("");
    }
  };
  const handleReset = () => {
    setSaleValue("");
    setVatRate("15");
    setIsSaleInclusiveOfVAT("false");
    setSaleValueError("");
    setVatRateError("");
    setVatResult("");
    setTotalValue("");
    setPriceBeforeVat("");
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const sale = parseFloat(saleValue);
    const rate = parseFloat(vatRate);
    if (isNaN(sale) || isNaN(rate)) {
      return;
    }
    if (isSaleInclusiveOfVAT === "false") {
      // Add tax
      const vatAmount = sale * (rate / 100);
      const total = sale + vatAmount;
      setVatResult(vatAmount.toFixed(2));
      setTotalValue(total.toFixed(2));
      setPriceBeforeVat(sale.toFixed(2));
    } else {
      // Extract tax
      const priceWithoutVat = sale / (1 + rate / 100);
      const vatAmount = sale - priceWithoutVat;
      setVatResult(vatAmount.toFixed(2));
      setTotalValue(sale.toFixed(2));
      setPriceBeforeVat(priceWithoutVat.toFixed(2));
    }
  };
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Halaman Utama",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Kalkulator",
          item: "https://do-calculate.com/id/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Kalkulator Pajak Pertambahan Nilai",
          item: OG_URL,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Apa itu pajak pertambahan nilai?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Pajak pertambahan nilai (PPN) adalah pajak tidak langsung yang dikenakan pada konsumsi barang dan jasa di setiap tahap produksi dan distribusi. Diterapkan di lebih dari 160 negara di seluruh dunia.",
          },
        },
        {
          "@type": "Question",
          name: "Bagaimana cara menghitung pajak pertambahan nilai?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Untuk menghitung PPN: jika jumlah sebelum pajak, kalikan dengan tarif pajak (misalnya 15%), lalu tambahkan hasilnya ke jumlah awal. Jika jumlah sudah termasuk pajak, bagi dengan (1 + tarif pajak).",
          },
        },
        {
          "@type": "Question",
          name: "Berapa tarif PPN di Arab Saudi?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Tarif PPN di Kerajaan Arab Saudi adalah 15% mulai 1 Juli 2020.",
          },
        },
        {
          "@type": "Question",
          name: "Apa perbedaan antara menambah dan mengekstrak pajak?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Menambah pajak: ketika harga sebelum pajak dan Anda ingin mengetahui harga akhir. Mengekstrak pajak: ketika harga sudah termasuk pajak dan Anda ingin mengetahui nilai pajak dan harga sebelum pajak.",
          },
        },
        {
          "@type": "Question",
          name: "Apakah semua barang dan jasa dikenakan PPN?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Tidak, ada beberapa barang dan jasa yang dibebaskan atau dikenakan tarif nol, seperti: ekspor internasional, layanan kesehatan dan pendidikan pemerintah, beberapa layanan keuangan, dan beberapa jenis properti.",
          },
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Kalkulator Pajak Pertambahan Nilai",
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, faqSchema, calculatorSchema];
  };
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Pajak Pertambahan Nilai</strong> adalah alat gratis dan mudah digunakan untuk <strong>menghitung pajak</strong> dengan akurat. Anda dapat <strong>menambahkan pajak</strong> ke jumlah berapa pun atau <strong>mengekstraknya</strong> dari jumlah total. Cocok untuk individu dan perusahaan untuk menghitung <strong>pajak penjualan</strong> dan <strong>pajak pembelian</strong> di negara mana pun dengan tarif pajak yang berbeda.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds/>
              {/* Calculator Section */}
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    Hitung Pajak Pertambahan Nilai
                  </h2>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label  htmlFor="saleValue">
                      <strong>Nilai Penjualan / Pembelian:</strong>
                    </label>
                    <div className="d-flex align-items-center">
                      <input
                        id="saleValue"
                        type="number"
                        className="form-control"
                        value={saleValue}
                        onChange={handleSaleChange}
                        placeholder="Contoh: 1000"
                        step="0.01"
                        min="0"
                        required
                        style={{ maxWidth: "200px" }}
                      />
                    </div>
                    {saleValueError && (
                      <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                        {saleValueError}
                      </div>
                    )}
                  </div>
                  <div className="form-group mb-3">
                    <label  htmlFor="vatRate">
                      <strong>Tarif Pajak (%):</strong>
                    </label>
                    <div className="d-flex align-items-center">
                      <input
                        id="vatRate"
                        type="number"
                        className="form-control"
                        value={vatRate}
                        onChange={handleVatRateChange}
                        placeholder="Contoh: 15"
                        step="0.01"
                        min="0"
                        max="100"
                        required
                        style={{ maxWidth: "200px" }}
                      />
                      <span
                        style={{
                          marginRight: "10px",
                          padding: "8px 15px",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          backgroundColor: "#f9f9f9",
                          fontSize: "16px",
                        }}
                      >
                        %
                      </span>
                    </div>
                    {vatRateError && (
                      <div className="text-danger mt-1" style={{ fontSize: "14px" }}>
                        {vatRateError}
                      </div>
                    )}
                  </div>
                  <div className="form-group mb-4">
                    <label >
                      <strong>Apakah jumlah sudah termasuk PPN?</strong>
                    </label>
                    <div className="d-flex align-items-center" style={{ gap: "20px" }}>
                      <label style={{ cursor: "pointer", fontSize: "16px" }}>
                        <input
                          type="radio"
                          name="includeVat"
                          value="false"
                          checked={isSaleInclusiveOfVAT === "false"}
                          onChange={(e) => setIsSaleInclusiveOfVAT(e.target.value)}
                          style={{ marginLeft: "8px" }}
                        />
                        Tidak (Tambah Pajak)
                      </label>
                      <label style={{ cursor: "pointer", fontSize: "16px" }}>
                        <input
                          type="radio"
                          name="includeVat"
                          value="true"
                          checked={isSaleInclusiveOfVAT === "true"}
                          onChange={(e) => setIsSaleInclusiveOfVAT(e.target.value)}
                          style={{ marginLeft: "8px" }}
                        />
                        Ya (Ekstrak Pajak)
                      </label>
                    </div>
                  </div>
                  <hr />
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn bg-success text-white"
                      style={{ fontSize: "16px", padding: "10px 30px" }}
                    >
                      Hitung
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                       style={{ fontSize: "16px", padding: "10px 30px", marginRight: "10px" }}
                    >
                      Hapus

                    </button>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {vatResult && totalValue && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>Hasil</h3>
                    </div>
                    <div className="alert alert-success" style={{ fontSize: "16px" }}>
                      <table className="bmi-table" style={{ width: "100%" }}>
                        <tbody>
                          <tr>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#e7f3fe",
                                padding: "12px",
                              }}
                            >
                              <strong>Jumlah Sebelum Pajak:</strong>
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#e7f3fe",
                                padding: "12px",
                              }}
                            >
                              <strong style={{ fontSize: "18px", color: "#1a5f3f" }}>
                                {priceBeforeVat}
                              </strong>
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#fff3cd",
                                padding: "12px",
                              }}
                            >
                              <strong>Jumlah Pajak ({vatRate}%):</strong>
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#fff3cd",
                                padding: "12px",
                              }}
                            >
                              <strong style={{ fontSize: "18px", color: "#856404" }}>
                                {vatResult}
                              </strong>
                            </td>
                           </tr>
                           <tr>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#d1ecf1",
                                padding: "12px",
                              }}
                            >
                              <strong>Jumlah Total (Termasuk Pajak):</strong>
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                backgroundColor: "#d1ecf1",
                                padding: "12px",
                              }}
                            >
                              <strong style={{ fontSize: "20px", color: "#0c5460" }}>
                                {totalValue}
                              </strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              <strong>💡 Tips:</strong> <strong>Kalkulator Pajak</strong> ini mendukung semua tarif pajak yang digunakan di negara Arab dan internasional. Anda dapat menghitung pajak dengan tarif 5%, 10%, 15%, atau tarif lainnya sesuai negara Anda.
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content Section - due to length, keeping core structure but translated */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Pajak Pertambahan Nilai? Panduan Lengkap</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Pajak Pertambahan Nilai</strong> (PPN) adalah <strong>pajak tidak langsung</strong> yang dikenakan pada konsumsi barang dan jasa di setiap tahap produksi dan distribusi. Dianggap sebagai salah satu sumber pendapatan pemerintah terpenting di lebih dari <strong>160 negara</strong> di seluruh dunia.
                </p>
                {/* Additional educational content would continue here with Indonesian translations */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default VATCalculator;